import { Request } from '@src/api/request.js';
import { createErrorModal } from '@utils/modal-util.js';
import { saveAs } from 'file-saver';
import { cloneDeep } from 'lodash';
import { type Dictionary } from '@src/application/dictionary.js';
import { QueryParams } from '@src/api/interface/query-params.ts';

class ApiClient {

    activeRequests: Dictionary<Request> = {};

    get(url: string, queryParams: QueryParams = {}): Request {
        let first = true;
        for (const param in queryParams) {
            url += first ? '?' : '&';
            url += param + '=' + encodeURIComponent(queryParams[param]);
            first = false;
        }

        if (
            Object.keys(this.activeRequests)
                .includes(url) &&
                !this.activeRequests[url].isCancelled
        ) {
            return this.activeRequests[url];
        }
        const request = this._request(url, 'GET');
        this.activeRequests[url] = request;
        request
            .then(() => delete this.activeRequests[url])
            .catch(() => delete this.activeRequests[url]);
        return request;
    }

    async forEachGet<listType, responseTye>(
        url: (entry: listType) => string,
        list: Array<listType>,
        callback: (response: { data: responseTye }, entry: listType) => void,
    ) {
        const promises = [];
        for (const entry of list) {
            promises.push(apiClient.get(url(entry))
                .then((response) => callback(response, entry)));
        }
        await Promise.all(promises);
    }

    post(url: string, data?: any) {
        return this._request(url, 'POST', data);
    }

    patch(url: string, data: any = null, fieldsToRemove: string[] = [], removeDeepNulls = false) {
        if (data === null) {
            return this._request(url, 'PATCH');
        }
        return this._request(url, 'PATCH', ApiClient._cleanPatchData(data, fieldsToRemove, removeDeepNulls));
    }

    delete(url: string, data?: any) {
        return this._request(url, 'DELETE', data);
    }

    uploadFile(url: string, file: File, additionalData = null, fieldsToRemove = [], removeDeepNulls = false) {
        const formData = new FormData();
        formData.append('file', file);
        if (additionalData !== null) {
            formData.append('data', JSON.stringify(ApiClient._cleanPatchData(additionalData, fieldsToRemove, removeDeepNulls)));
        }
        return this._request(
            url,
            'POST',
            formData,
        );
    }

    uploadFiles(url: string, files: File[], additionalData: any|null = null, fieldsToRemove = [], removeDeepNulls = false) {
        const formData = new FormData();
        for (const file of files) {
            formData.append('files[]', file);
        }
        if (additionalData !== null) {
            formData.append('data', JSON.stringify(ApiClient._cleanPatchData(additionalData, fieldsToRemove, removeDeepNulls)));
        }
        return this._request(
            url,
            'POST',
            formData,
        );
    }

    async getBinarySafe(url: string, method?: 'GET'|'POST', data?: any) {
        const response = await this._request(url, method ?? 'GET', data);
        const fileName: string|null = ApiClient._getFileNameFromContentDisposition(response.headers['content-disposition']);
        const fileContent: Blob = response.data;
        return { fileName: fileName, fileContent: <Blob>fileContent };
    }

    async downloadFile(url: string) {
        const { fileName, fileContent } = await this.getBinarySafe(url);
        if (fileName === null) {
            return;
        }
        saveAs(fileContent, fileName);
    }

    // see https://stackoverflow.com/a/67994693
    static _getFileNameFromContentDisposition(disposition: string) {
        const utf8FilenameRegex = /filename\*=UTF-8''([\w%\-.]+)(?:; ?|$)/i;
        const asciiFilenameRegex = /filename=(["']?)(.*?[^\\])\1(?:; ?|$)/i;
        let fileName = null;
        if (utf8FilenameRegex.test(disposition)) {
            const matched = utf8FilenameRegex.exec(disposition);
            if (matched !== null) {
                fileName = decodeURIComponent(matched[1]);
            }
        } else {
            const matches = asciiFilenameRegex.exec(disposition);
            if (matches !== null && matches[2]) {
                fileName = matches[2];
            }
        }
        return fileName;
    }

    static _cleanPatchData(data: any, fieldsToRemove: string[] = [], removeDeepNulls = false) {
        const patchData = cloneDeep(data);
        for (const field of fieldsToRemove) {
            delete patchData[field];
        }
        if (removeDeepNulls) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
            const isAllNull: Function = (obj: object) => {
                if (obj === null) {
                    return true;
                }
                if (typeof obj !== 'object') {
                    return false;
                }
                return Object.values(obj)
                    .every((value) => {
                        if (value === null) {
                            return true;
                        }
                        if (typeof value === 'object') {
                            return isAllNull(value);
                        }
                        return false;
                    });
            };
            for (const prop in patchData) {
                if (isAllNull(patchData[prop])) {
                    patchData[prop] = null;
                }
            }
        }
        return patchData;
    }

    _request(url: string, method: 'GET'|'POST'|'PATCH'|'DELETE', data: any = null): Request {
        const request = new Request(url, method, data);
        request.fetch();
        return request;
    }
}

const apiClient = new ApiClient();

export default apiClient;

export function createApiErrorModal(error: { message: string }) {
    return createErrorModal('Ein Fehler ist aufgetreten', error.message);
}
